/**
 * Renderer seam: read-only accessors over display objects for the (browser) Canvas renderer,
 * so that src/flash/render/** never has to touch `$`-internals directly.
 *
 * Tree walk: `renderChildren(o)` gives what to draw under `o` (containers: render list; buttons:
 * the current state object). Per object: `localMatrix`, `colorTransform`, `alpha`, `isVisible`,
 * `blendMode`, `filters`, `mask`/`isMask`, `clipDepth`/`depth` (timeline clip layers: a child with
 * clipDepth > 0 masks siblings with depth in (depth, clipDepth]), `scrollRect`, `cacheAsBitmap`,
 * `opaqueBackground`. Content: `shapeImage` (rasterized ImageRef + decoded image), `bitmapOf`
 * (BitmapData pixels, `$version` for re-upload, `$takeDirty()` for partial upload), `textInfo`
 * (TextField layout lines/runs, border/background, scroll offsets, caret/selection),
 * `graphicsCommands` (vector drawing commands of Shape/Sprite graphics).
 */
import type { DisplayObject, RenderKind } from './DisplayObject';
import type { GraphicsCommand } from './Graphics';
import type { ImageForRender } from '../symbols/types';
import type { BitmapFilter } from '../filters/filters';
import { rt } from '../player/runtime';
import { tmatRect } from '../utils/numeric';

export function renderKind(o: DisplayObject): RenderKind {
  return o.$renderKind;
}

export function renderChildren(o: DisplayObject): DisplayObject[] {
  const k = o.$renderKind;
  if (k === 'button') {
    const s = (o as any).$currentStateObject();
    return s ? [s] : [];
  }
  return (o as any).$isContainer ? (o as any).$children : [];
}

/** Local matrix in pixels: [a, b, c, d, tx, ty]. */
export function localMatrix(o: DisplayObject): [number, number, number, number, number, number] {
  const m = o.$m;
  return [m.a, m.b, m.c, m.d, m.tx / 20, m.ty / 20];
}

/** Colour transform [rm, gm, bm, am, ro, go, bo, ao] (multipliers as reals, offsets -255..255). */
export function colorTransform(o: DisplayObject): [number, number, number, number, number, number, number, number] {
  return o.$colorTransformArray();
}

export function alpha(o: DisplayObject): number {
  return o.$ctAM / 256;
}

export function isVisible(o: DisplayObject): boolean {
  return o.$visible;
}

export function blendMode(o: DisplayObject): string {
  return o.$blendMode;
}

export function filters(o: DisplayObject): readonly BitmapFilter[] {
  return o.$filters;
}

export function mask(o: DisplayObject): DisplayObject | null {
  return o.$mask;
}

/** The object `o` masks (via `.mask`), or null. */
export function maskeeOf(o: DisplayObject): DisplayObject | null {
  return o.$maskee;
}

/** True when `o` is used as a mask (via `.mask`) and must not be drawn normally. */
export function isMask(o: DisplayObject): boolean {
  return !!o.$maskee;
}

export function clipDepth(o: DisplayObject): number {
  return o.$clipDepth;
}

export function depth(o: DisplayObject): number {
  return o.$depth;
}

export function scrollRect(o: DisplayObject): { x: number; y: number; width: number; height: number } | null {
  const r = o.$scrollRect;
  return r ? { x: r.x, y: r.y, width: r.width, height: r.height } : null;
}

export function cacheAsBitmap(o: DisplayObject): boolean {
  return o.cacheAsBitmap;
}

export function opaqueBackground(o: DisplayObject): number | null {
  return o.$opaqueBackground;
}

export function shapeImage(o: DisplayObject): ImageForRender | null {
  const f = (o as any).$imageForRender;
  return typeof f === 'function' ? f.call(o) : null;
}

export function bitmapOf(o: DisplayObject): { bitmapData: any; smoothing: boolean; pixelSnapping: string } | null {
  if (o.$renderKind !== 'bitmap') return null;
  const b: any = o;
  return b.$bitmapData && !b.$bitmapData.$disposed ? { bitmapData: b.$bitmapData, smoothing: b.$smoothing, pixelSnapping: b.$pixelSnapping } : null;
}

export function textInfo(o: DisplayObject): ReturnType<any> | null {
  return o.$renderKind === 'text' ? (o as any).$renderInfo() : null;
}

export function graphicsCommands(o: DisplayObject): readonly GraphicsCommand[] | null {
  const g = (o as any).$graphics;
  return g ? g.$commands : null;
}

/** Per-object render invalidation counter (changes whenever the object's own state changes). */
export function versionOf(o: DisplayObject): number {
  return o.$version;
}

/**
 * Axis-aligned bounds (px) of `o` (self + children, as getBounds computes them) under the pixel
 * matrix `m` = [a, b, c, d, tx, ty] mapping o's local space; null when empty. Used by the renderer
 * to size offscreen layers (masks, filters, blend modes) and for culling.
 */
export function boundsWithMatrix(o: DisplayObject, m: readonly number[]): { xMin: number; yMin: number; xMax: number; yMax: number } | null {
  const r = o.$boundsWithTransform({ a: m[0], b: m[1], c: m[2], d: m[3], tx: Math.round(m[4] * 20), ty: Math.round(m[5] * 20) });
  if (!(r.xMin <= r.xMax && r.yMin <= r.yMax)) return null;
  return { xMin: r.xMin / 20, yMin: r.yMin / 20, xMax: r.xMax / 20, yMax: r.yMax / 20 };
}

/** Local -> stage matrix in pixels [a, b, c, d, tx, ty] (includes ancestors' scrollRect offsets). */
export function globalMatrix(o: DisplayObject): [number, number, number, number, number, number] {
  const m = o.$localToGlobalMatrix();
  return [m.a, m.b, m.c, m.d, m.tx / 20, m.ty / 20];
}

/** Axis-aligned bounds (px) of `o`'s own content (no children) under the pixel matrix `m`; null when empty. */
export function selfBoundsWithMatrix(o: DisplayObject, m: readonly number[]): { xMin: number; yMin: number; xMax: number; yMax: number } | null {
  const r = tmatRect({ a: m[0], b: m[1], c: m[2], d: m[3], tx: Math.round(m[4] * 20), ty: Math.round(m[5] * 20) }, o.$selfBounds());
  if (!(r.xMin <= r.xMax && r.yMin <= r.yMax)) return null;
  return { xMin: r.xMin / 20, yMin: r.yMin / 20, xMax: r.xMax / 20, yMax: r.yMax / 20 };
}

// ------------------------------------------------------------------ PORT: damage rendering

/** Starts (or stops) collecting what changes on the display list for a damage renderer (src/flash/render/damage.ts). */
export function trackChanges(on: boolean): void {
  const r = rt();
  if (on) {
    r.changes ??= [];
    r.changedBitmaps ??= [];
  } else {
    for (const o of r.changes ?? []) o.$dflags = 0;
    for (const b of r.changedBitmaps ?? []) b.$dchanged = false;
    r.changes = null;
    r.changedBitmaps = null;
  }
}

export interface Changes {
  /** Objects changed since the last take, and how (DAMAGE_SELF / DAMAGE_CHILDREN bits), index for index. */
  objects: DisplayObject[];
  flags: number[];
  /** BitmapData whose pixels changed. */
  bitmaps: any[];
}

/** Takes (and clears) the changes collected since the last take. */
export function takeChanges(): Changes {
  const r = rt();
  const objects: DisplayObject[] = r.changes ?? [];
  const bitmaps: any[] = r.changedBitmaps ?? [];
  const flags = new Array<number>(objects.length);
  for (let i = 0; i < objects.length; i++) {
    flags[i] = objects[i].$dflags;
    objects[i].$dflags = 0;
  }
  for (const b of bitmaps) b.$dchanged = false;
  if (r.changes) r.changes = [];
  if (r.changedBitmaps) r.changedBitmaps = [];
  return { objects, flags, bitmaps };
}

/** The object is a stage. */
export function isStage(o: DisplayObject): boolean {
  return !!(o as any).$isStage;
}

/** The object's parent (for a button's state: the button). */
export function parentOf(o: DisplayObject): DisplayObject | null {
  return (o as any).$parent ?? null;
}
